/**
 * New-chart wizard (W2-14 onboarding-wizard; DESIGN.md §4.3, §5.1; `DIALOG.wizard`): a modal stepper over the
 * registered wizard steps in order: Song, Picture, Details, Beat (from the timing package, when present), Start.
 *
 *  - Every step has Back; every skippable step has "Skip for now". A skipped step is recorded in the draft and
 *    becomes a Problems tip, never a silent default: Picture -> placeholder illustration (ST-02), Beat -> 120 BPM and
 *    beat 0 at 0 s, unconfirmed (ST-01), Details -> empty fields (ST-09; a preview range outside the song goes back
 *    to the default, `skipPatch`). Continuing normally clears the mark.
 *  - Next stays disabled (with the reason) until the step says it can continue (`setCanContinue`).
 *  - The header lists the steps with their state; finished steps can be revisited by clicking them.
 *  - Create (last step) builds the project (createProject.ts), closes the dialog with `{ projectId }`, then opens the
 *    Chart workspace and offers the tour. The project is opened after the dialog closed because opening replaces the
 *    current project, which dismisses dialogs.
 *  - Closing with a song chosen asks before discarding.
 * Media dropped on the Library arrive pre-filled (`WizardProps.music` / `.illustration`); the Song step decodes the
 * song and starts the analysis as if it had been chosen there.
 */

import { useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { DialogProps } from '../../app/registry/types';
import type { NewChartDraft, WizardStepDef, WizardStepId } from '../../app/registry/types';
import type { ProjectId } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import type { ImportedAsset } from '../../formats/types';
import { Button, Dialog, Icon } from '../../ui';
import { createProjectFromDraft, isFirstProject, NewChartError, openNewProject } from './createProject';
import {
  baseName,
  CHARTER_EXT_KEY,
  defaultPreviewRange,
  initialDraft,
  previewRangeProblem,
  withSkipped,
} from './draft';
import { m } from './wizard.messages';
import s from './Wizard.module.css';
import { useServicesText, WizardSession, WizardSessionProvider } from './wizardSession';
import { WIZARD_STEPS } from './wizardSteps';

/** Steps in order: the registry's (every module's), else this package's own. */
export function wizardStepsOf(registered: readonly WizardStepDef[]): readonly WizardStepDef[] {
  const list = registered.length > 0 ? registered : WIZARD_STEPS;
  return [...list].sort((a, b) => a.order - b.order);
}

/**
 * Draft fields a skipped step resets (DESIGN.md §4.3: a skipped step never keeps half-made choices), given the
 * draft at the moment of skipping. Details keeps its texts (pre-filled or typed; empty ones become ST-09 tips) but
 * never a song-select preview range that is not inside the song: that range goes back to the song's default, so
 * Create cannot fail on a step the user chose to skip.
 */
export function skipPatch(step: WizardStepId, draft: NewChartDraft): Partial<NewChartDraft> {
  switch (step) {
    case 'picture':
      return { illustration: null };
    case 'beat':
      // The detected (or typed) tempo and first beat stay, unconfirmed: the chart reminds the user (UX-075).
      return { timingConfirmed: false };
    case 'details': {
      const duration = draft.music?.durationSec ?? 0;
      if (previewRangeProblem(draft.previewStart, draft.previewEnd, duration) === null) return {};
      const range = defaultPreviewRange(duration);
      return { previewStart: range.start, previewEnd: range.end };
    }
    case 'song':
    case 'start':
      return {};
  }
}

/**
 * A skippable step whose choice is made: "Skip for now" is not offered then (it would discard the choice, UX-085).
 * Details always has its pre-filled texts, so it keeps the button (skipping there keeps them).
 */
export function stepComplete(step: WizardStepId, draft: NewChartDraft): boolean {
  switch (step) {
    case 'picture':
      return draft.illustration !== null && !draft.illustration.placeholder;
    case 'beat':
      return draft.timingConfirmed;
    default:
      return false;
  }
}

function musicOf(a: ImportedAsset): NonNullable<NewChartDraft['music']> {
  return {
    bytes: a.bytes,
    sha256: a.id,
    name: baseName(a.path),
    mime: a.mime,
    durationSec: a.meta.durationSec ?? 0,
    converted: false,
  };
}

/** The draft a run starts from (remembered charter, media dropped on the Library). */
export function seedDraft(props: DialogProps<'wizard'>['props'], charter: string, guided = false): NewChartDraft {
  const base = initialDraft({ charter, guided });
  return {
    ...base,
    ...(props.music === undefined ? {} : { music: musicOf(props.music) }),
    ...(props.illustration === undefined
      ? {}
      : {
          illustration: {
            bytes: props.illustration.bytes,
            name: baseName(props.illustration.path),
            mime: props.illustration.mime,
            placeholder: false,
          },
        }),
  };
}

type StepState = 'done' | 'skipped' | 'current' | 'todo';

export function WizardDialog(p: DialogProps<'wizard'>) {
  const { services, props } = p;
  const close = (result: { readonly projectId: ProjectId } | null): void => {
    p.close(result);
  };
  const text = useServicesText(services);
  const [session] = useState(() => {
    const charter = services.settings.getExt(CHARTER_EXT_KEY);
    const guided = services.settings.get('general', 'level') === 'guided';
    return new WizardSession(seedDraft(props, typeof charter === 'string' ? charter : '', guided));
  });
  const draft = useSyncExternalStore(session.subscribe, session.draft, session.draft);
  const update = session.update;
  const steps = useMemo(() => wizardStepsOf(services.registry.wizardSteps()), [services]);
  const [index, setIndex] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [canContinue, setCanContinueMap] = useState<Readonly<Record<string, boolean>>>({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closing = useRef(false);

  const setters = useMemo(
    () =>
      new Map(
        steps.map((st) => [
          st.id,
          (ok: boolean) => {
            setCanContinueMap((prev) => (prev[st.id] === ok ? prev : { ...prev, [st.id]: ok }));
          },
        ]),
      ),
    [steps],
  );
  const noop = useMemo(() => (_ok: boolean) => undefined, []);

  const step = steps[index];
  const last = index === steps.length - 1;
  const stepOk = step === undefined ? false : (canContinue[step.id] ?? step.id !== 'song');

  const go = (to: number): void => {
    const target = Math.max(0, Math.min(steps.length - 1, to));
    setIndex(target);
    setFurthest((f) => Math.max(f, target));
    setError(null);
  };

  const create = async (): Promise<void> => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const firstProject = await isFirstProject(services);
      const id = await createProjectFromDraft(services, session.draft(), {
        untitled: text.t(m.untitled),
        names: {
          main: text.t(m.lineMain),
          second: text.t(m.lineSecond),
          numbered: (n) => text.t(m.lineNumbered, { n }),
        },
      });
      closing.current = true;
      close({ projectId: id });
      void openNewProject(services, id, { firstProject }).catch((e: unknown) => {
        services.ui.toast(
          { key: m.errorCreate, params: { detail: e instanceof Error ? e.message : String(e) } },
          { kind: 'error' },
        );
      });
    } catch (e) {
      const detail =
        e instanceof NewChartError ? services.i18n.tr(e.reason) : e instanceof Error ? e.message : String(e);
      setError(text.t(m.errorCreate, { detail }));
      setCreating(false);
    }
  };

  const next = (): void => {
    if (step === undefined || !stepOk) return;
    const cur = session.draft();
    if (cur.skipped.includes(step.id)) update({ skipped: withSkipped(cur.skipped, step.id, false) });
    if (last) void create();
    else go(index + 1);
  };

  const skip = (): void => {
    if (step === undefined || !step.skippable) return;
    const cur = session.draft();
    update({ ...skipPatch(step.id, cur), skipped: withSkipped(cur.skipped, step.id, true) });
    if (last) void create();
    else go(index + 1);
  };

  const dismiss = (): void => {
    if (creating || closing.current) return;
    if (session.draft().music === null) {
      closing.current = true;
      close(null);
      return;
    }
    void services.ui
      .confirm({
        title: { key: m.discardTitle },
        body: { key: m.discardBody },
        confirm: { key: m.discardConfirm },
        danger: true,
      })
      .then((ok) => {
        if (ok && !closing.current) {
          closing.current = true;
          close(null);
        }
      });
  };

  const stateOf = (i: number, id: WizardStepId): StepState => {
    if (i === index) return 'current';
    if (draft.skipped.includes(id)) return 'skipped';
    return i < furthest || i < index ? 'done' : 'todo';
  };

  const nextTitle = steps[index + 1];
  const blocked: MessageRef | null = stepOk ? null : { key: step?.id === 'song' ? m.nextBlockedSong : m.nextBlocked };

  const footer = (
    <div className={s.footer}>
      <Button variant="ghost" onClick={dismiss} disabledReason={creating ? { key: m.creating } : null}>
        {text.t(m.cancel)}
      </Button>
      <span className={s.footerSpacer} />
      <Button
        icon="ChevronLeft"
        onClick={() => {
          go(index - 1);
        }}
        disabledReason={creating ? { key: m.creating } : index === 0 ? { key: m.firstStep } : null}
      >
        {text.t(m.back)}
      </Button>
      {step?.skippable === true && !stepComplete(step.id, draft) && (
        <Button icon="SkipForward" onClick={skip} disabledReason={creating ? { key: m.creating } : null}>
          {text.t(m.skip)}
        </Button>
      )}
      <Button
        variant="primary"
        icon={last ? 'Check' : 'ChevronRight'}
        onClick={next}
        disabledReason={creating ? { key: m.creating } : blocked}
      >
        {creating
          ? text.t(m.creating)
          : last
            ? text.t(m.create)
            : text.t(m.next, { step: nextTitle === undefined ? '' : text.t(nextTitle.title) })}
      </Button>
    </div>
  );

  const Component = step?.component;
  return (
    <Dialog open title={text.t(m.title)} onClose={dismiss} footer={footer} size="lg">
      <WizardSessionProvider value={session}>
        <nav aria-label={text.t(m.stepsLabel)}>
          <ol className={s.stepper}>
            {steps.map((st, i) => {
              const state = stateOf(i, st.id);
              const reachable = !creating && i !== index && (i < index || (i <= furthest && stepOk));
              return (
                <li key={st.id} className={s.stepItem} data-state={state}>
                  <button
                    type="button"
                    className={s.stepButton}
                    aria-current={i === index ? 'step' : undefined}
                    tabIndex={i === index ? -1 : undefined}
                    aria-label={text.t(m.stepState, { n: i + 1, title: text.t(st.title), state })}
                    disabled={!reachable && i !== index}
                    onClick={() => {
                      if (reachable) go(i);
                    }}
                  >
                    <span className={s.stepNumber} aria-hidden="true">
                      {state === 'done' ? (
                        <Icon name="Check" size={12} />
                      ) : state === 'skipped' ? (
                        <Icon name="SkipForward" size={12} />
                      ) : (
                        String(i + 1)
                      )}
                    </span>
                    <span className={s.stepTitle}>{text.t(st.title)}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
        {error !== null && (
          <p className={s.error} role="alert">
            <Icon name="CircleX" size={16} />
            <span>{error}</span>
          </p>
        )}
        <section className={s.stepBody} aria-label={step === undefined ? undefined : text.t(step.title)}>
          {Component !== undefined && step !== undefined && (
            <Component
              key={step.id}
              services={services}
              draft={draft}
              update={update}
              setCanContinue={setters.get(step.id) ?? noop}
            />
          )}
        </section>
      </WizardSessionProvider>
    </Dialog>
  );
}
